import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ThemeContext } from '@/contexts/ThemeContext/ThemeContext';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import {
    HUB_TRANSITION_TIMEOUT_MS,
    getHubStaticSrc,
    loadHubTransition,
    type HubColor,
} from '@/components/Hub/hubIconAssets';
import {
    beginHubIconAnimation,
    endHubIconAnimation,
} from '@/components/FloatingActionBar/iconAnimationCoordinator';

export interface HubIconTransition {
    /** The color currently displayed at rest, and the one the static image shows. */
    restingColor: HubColor;
    /** Static image source for the resting color in the active theme. */
    staticSrc: string;
    /** Transition animation data, or null when nothing is playing. */
    animationData: unknown | null;
    /** Call when the playing animation reports completion. */
    onAnimationComplete: () => void;
}

// First render never animates. The icon adopts whatever color the initial route implies, so a
// hard refresh onto `/skills` shows green rather than animating in from the default.
export const useHubIconTransition = (targetColor: HubColor): HubIconTransition => {
    // Read the context directly rather than via useTheme(), which throws outside a ThemeProvider.
    // The icon must not be the reason a subtree can't be mounted on its own.
    const theme = useContext(ThemeContext)?.theme ?? 'light';
    const prefersReducedMotion = usePrefersReducedMotion();

    const [restingColor, setRestingColor] = useState<HubColor>(targetColor);
    const [animationData, setAnimationData] = useState<unknown | null>(null);

    // Where the in-flight transition is headed. Read by the completion handler, which must not
    // re-run when it changes so it lives in a ref rather than state.
    const pendingTargetRef = useRef<HubColor | null>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const settle = useCallback(() => {
        if (timeoutRef.current !== null) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        const pending = pendingTargetRef.current;
        pendingTargetRef.current = null;
        setAnimationData(null);
        if (pending) {
            setRestingColor(pending);
        }
    }, []);

    useEffect(() => {
        if (targetColor === restingColor && pendingTargetRef.current === null) {
            return;
        }
        if (pendingTargetRef.current === targetColor) {
            return;
        }

        if (prefersReducedMotion) {
            // Skip the fetch entirely and jump straight to the new color.
            pendingTargetRef.current = null;
            setAnimationData(null);
            setRestingColor(targetColor);
            return;
        }

        // Always transition from the color actually on screen. Rapid navigation replaces the
        // pending pair rather than chaining from a color that was never displayed.
        const from = restingColor;
        const to = targetColor;
        if (from === to) {
            return;
        }

        let cancelled = false;
        pendingTargetRef.current = to;

        loadHubTransition(from, to, theme).then((data) => {
            if (cancelled) {
                return;
            }
            if (!data) {
                // No asset for this pair so snap rather than sit on the old color.
                settle();
                return;
            }
            setAnimationData(data);
            // Backstop for dotLottie never emitting `complete` when it fails to render, which
            // would strand the icon mid-transition.
            timeoutRef.current = setTimeout(settle, HUB_TRANSITION_TIMEOUT_MS);
        });

        return () => {
            cancelled = true;
        };
    }, [targetColor, restingColor, theme, prefersReducedMotion, settle]);

    // Tells the dock a Hub animation is running so ZigBot's nudge stands aside for it.
    useEffect(() => {
        if (animationData === null) {
            return;
        }
        beginHubIconAnimation();
        return endHubIconAnimation;
    }, [animationData]);

    useEffect(() => () => {
        if (timeoutRef.current !== null) {
            clearTimeout(timeoutRef.current);
        }
    }, []);

    return {
        restingColor,
        staticSrc: getHubStaticSrc(restingColor, theme),
        animationData,
        onAnimationComplete: settle,
    };
};
